// @ts-nocheck
import { lazy, Suspense, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Route, Router, Switch, useLocation } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client/react';
import { connect, Provider } from 'react-redux';
import * as themes from '@feast-it/pesto';
import { ThemeProvider } from 'emotion-theming';
import queryString from 'query-string';

import ReactGA from 'react-ga4';
import { resetMetadata } from './actions/metadata';
import LayoutSkeleton from './components-togather/layout-skeleton';
import { history as baseHistory } from './modules/history';
import { client } from './modules/apollo';
import { useUser } from './hooks/useUser';
import { GCLID, MSCLKID } from './constants/session-storage';
import { useCookieHubConsent } from './hooks/useCookieHubConsent';
import { trackPageViewedClientSide } from './modules/analytics/analytics';
import { CustomerIo } from './components-togather/CustomerIo';
import { isProductionEnv } from './libs/env';
import { FrontChat } from './components-togather/FrontChat';

const URLRedirect = lazy(() => import('./components-togather/url-redirect'));
const Dashboard = lazy(() => import('./containers-togather/pages/dashboard'));
const SharedQuoteView = lazy(
  () => import('./containers-togather/pages/SharedQuoteView/SharedQuoteView')
);
const UserRoute = lazy(() => import('./router/user-route'));
const CustomerRoute = lazy(() => import('./router/customer-route'));
const VisitorRoute = lazy(() => import('./router/visitor-route'));
const Shell = lazy(() => import('./containers-togather/partials/shell'));
const Missing = lazy(() => import('./containers-togather/pages/missing'));
const Login = lazy(
  () => import('./containers-togather/pages/LoginPage/LoginPage')
);
const Booking = lazy(() => import('./containers-togather/pages/booking'));
const PaymentConfirmation = lazy(
  () =>
    import('./containers-togather/pages/PaymentConfirmation/PaymentConfirmation')
);
const MyBookings = lazy(
  () => import('./containers-togather/pages/my-bookings')
);
const MyPayments = lazy(
  () => import('./containers-togather/pages/my-payments')
);
const MyMessages = lazy(() => import('./containers-togather/pages/messages'));
const Settings = lazy(
  () => import('./containers-togather/pages/settings/settings')
);
const ExternalReviews = lazy(
  () => import('./containers-togather/pages/external-reviews')
);
const MyFavourites = lazy(
  () => import('./containers-togather/pages/my-favourites')
);
const ImpersonatePage = lazy(
  () => import('./containers-togather/pages/ImpersonatePage')
);
// Supplier Referral Page needs to be moved to olympus
const SupplierRoute = lazy(() => import('./router/supplier-route'));
const SuppliersReferral = lazy(
  () => import('./containers-togather/pages/suppliers-dashboard/referral')
);

const mapDispatchToProps = {
  onResetMetadata: resetMetadata,
};

const Routes = connect(
  null,
  mapDispatchToProps
)(({ onResetMetadata }) => {
  const location = useLocation();
  const queries = queryString.parse(location.search);
  const user = useUser();

  useCookieHubConsent({ queries });

  const { pathname, search } = location;
  const page = `${pathname}${search}`;

  useEffect(() => {
    ReactGA?.set({ page });
    trackPageViewedClientSide({ location });
  }, [page]);

  useEffect(() => {
    const gclid = queries?.gclid;
    const msclkid = queries?.msclkid;

    if (!sessionStorage.getItem(GCLID) && gclid) {
      sessionStorage.setItem(GCLID, gclid as string);
    }

    if (!sessionStorage.getItem(MSCLKID) && msclkid) {
      sessionStorage.setItem(MSCLKID, msclkid as string);
    }
  }, [queries]);

  // Reset scroll position and metadata when pathname changes
  useEffect(() => {
    window.scrollTo(0, 0);
    onResetMetadata();
  }, [location.pathname, onResetMetadata]);

  return !user ? (
    <LayoutSkeleton />
  ) : (
    <ThemeProvider theme={themes.togatherTheme}>
      <Shell>
        <Switch>
          <CustomerRoute path="/dashboard" component={Dashboard} />
          <CustomerRoute path="/my-bookings" component={MyBookings} />
          <CustomerRoute
            path="/booking/:bookingId/payment"
            component={Booking}
          />
          <CustomerRoute
            path="/payment/confirmation/:bookingId"
            component={PaymentConfirmation}
          />
          <UserRoute path="/messages" component={MyMessages} />
          <VisitorRoute path="/login" component={Login} />
          <CustomerRoute path="/my-payments" component={MyPayments} />
          <UserRoute path="/settings" component={Settings} />
          <Route path="/u/:urlId" component={URLRedirect} />
          <Route path="/my-quote/:quoteId" component={SharedQuoteView} />
          <VisitorRoute
            exact
            path="/reviews/:reviewId"
            component={ExternalReviews}
          />
          <CustomerRoute path="/my-favourites" component={MyFavourites} />
          <SupplierRoute
            path="/suppliers/dashboard/refer"
            component={SuppliersReferral}
          />
          <Route
            path="/impersonate/:authorizationToken"
            component={ImpersonatePage}
          />
          <Route path="*" component={Missing} />
        </Switch>
      </Shell>
    </ThemeProvider>
  );
});

export function App({ store }) {
  return (
    <ApolloProvider client={client}>
      <Suspense fallback={<LayoutSkeleton />}>
        <Provider store={store}>
          <Router history={baseHistory}>
            <Routes />

            {!isProductionEnv() && <CustomerIo />}

            <FrontChat />
          </Router>
        </Provider>
      </Suspense>
    </ApolloProvider>
  );
}

App.propTypes = {
  store: PropTypes.shape({}).isRequired,
};
